/**
 * Form Modal Component
 * Modal wrapper for forms with validation and submission
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'antd';

/**
 * FormModal Component
 *
 * @param {boolean} visible - Modal visibility (from useModal)
 * @param {function} onSubmit - Form submit handler
 * @param {function} onCancel - Cancel handler
 * @param {string} title - Modal title
 * @param {ReactNode} children - Form fields
 * @param {object} initialValues - Initial form values
 * @param {object} formProps - Ant Design Form props
 * @param {string} submitText - Submit button text (default: 'Submit')
 * @param {string} cancelText - Cancel button text (default: 'Cancel')
 * @param {string} width - Modal width (default: 600)
 * @param {boolean} destroyOnClose - Destroy form on close (default: true)
 *
 * @example
 * const modal = useModal();
 * <FormModal
 *   visible={modal.visible}
 *   onCancel={modal.close}
 *   onSubmit={handleSubmit}
 *   title="Create Project"
 *   initialValues={modal.data}
 * >
 *   <Form.Item name="name" label="Name" rules={[{ required: true }]}>
 *     <Input />
 *   </Form.Item>
 * </FormModal>
 */
function FormModal({
  visible,
  onSubmit,
  onCancel,
  title,
  children,
  initialValues = {},
  formProps = {},
  submitText = 'Submit',
  cancelText = 'Cancel',
  width = 600,
  destroyOnClose = true,
  ...restProps
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [visible, initialValues, form]);

  // Handle form submission
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (onSubmit) {
        await onSubmit(values);
      }

      // Close modal after successful submission
      if (onCancel) {
        onCancel();
      }
    } catch (error) {
      // Validation error - don't close modal
      console.error('Form validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={title}
      onOk={handleOk}
      onCancel={onCancel}
      okText={submitText}
      cancelText={cancelText}
      width={width}
      destroyOnClose={destroyOnClose}
      confirmLoading={loading}
      centered
      {...restProps}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        {...formProps}
      >
        {children}
      </Form>
    </Modal>
  );
}

export default FormModal;
